import React, { useId } from 'react';

export interface PersonDetailsValues {
  name: string;
  email: string;
  phone: string;
  whatsapp: string;
  notes: string;
}

export type PersonDetailsErrors = Partial<Record<keyof PersonDetailsValues, string>>;

interface PersonDetailsSectionProps {
  values: PersonDetailsValues;
  errors: PersonDetailsErrors;
  /** An error from the server that is not about one field. */
  serverError: string | null;
  disabled: boolean;
  /** Said under the e-mail field (who may change it). */
  emailHint?: string;
  onChange: (patch: Partial<PersonDetailsValues>) => void;
}

/** Name, e-mail, phone, WhatsApp and notes of one person. */
const PersonDetailsSection: React.FC<PersonDetailsSectionProps> = ({ values, errors, serverError, disabled, emailHint, onChange }) => {
  const uid = useId();
  const field = (key: keyof PersonDetailsValues, label: string, type: string, opts: { required?: boolean; hint?: string } = {}) => {
    const error = errors[key];
    const describedBy = [error ? `${uid}-${key}-error` : '', opts.hint ? `${uid}-${key}-hint` : ''].filter(Boolean).join(' ') || undefined;
    return (
      <div className="form-group">
        <label htmlFor={`${uid}-${key}`}>{label}{opts.required && <> <span className="form-required">*</span></>}</label>
        <input
          id={`${uid}-${key}`}
          type={type}
          value={values[key]}
          disabled={disabled}
          aria-required={opts.required || undefined}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          onChange={e => onChange({ [key]: e.target.value })}
        />
        {opts.hint && <p id={`${uid}-${key}-hint`} className="form-hint">{opts.hint}</p>}
        {error && <p id={`${uid}-${key}-error`} className="form-error-text" role="alert">{error}</p>}
      </div>
    );
  };

  return (
    <section className="settings-card ep-section" aria-labelledby={`${uid}-title`}>
      <h2 id={`${uid}-title`} className="ap-section-title">Person details</h2>
      <div className="ep-fields">
        {field('name', 'Name', 'text', { required: true })}
        {field('email', 'Email', 'email', { hint: emailHint })}
        {field('phone', 'Phone', 'tel')}
        {field('whatsapp', 'WhatsApp', 'tel')}
        <div className="form-group ep-fields__wide">
          <label htmlFor={`${uid}-notes`}>Notes</label>
          <textarea id={`${uid}-notes`} rows={3} value={values.notes} disabled={disabled} onChange={e => onChange({ notes: e.target.value })} />
        </div>
      </div>
      {serverError && <p className="form-error-text" role="alert">{serverError}</p>}
    </section>
  );
};

export default PersonDetailsSection;
